import { format } from "date-fns";

export function convertToHHMMSS(time: string) {
    // Regular expression to match HH:mm format
    const hhmmRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    // Regular expression to match HH:mm:ss format
    const hhmmssRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;

    if (hhmmRegex.test(time)) {
        return `${time}:00`; // Append ":00" to convert to HH:mm:ss
    } else if (hhmmssRegex.test(time)) {
        return time; // Return as is
    } else {
        return null; // Handle invalid input
    }
}

// convertToHHMMSS("12:34:00") => "12:34"
export function convertToHHMM(time: string) {
    // Regular expression to match HH:mm:ss format
    const hhmmssRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    // Regular expression to match HH:mm format
    const hhmmRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

    if (hhmmRegex.test(time)) {
        return time; // Return as is
    } else if (hhmmssRegex.test(time)) {
        return time.slice(0, 5); // Return only HH:mm part
    } else {
        return null; // Handle invalid input
    }
}

export function convertTimeAMPMToHHMMSS(time: string) {
    const timePattern = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
    const match = time.match(timePattern);
    if (!match) {
        // condition on Invalid time format. Expected format is HH:MM AM/PM.
        return time;
    }

    let [, hourStr, minuteStr, period] = match;
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    period = period.toUpperCase();

    // Convert hour to 24-hour format
    if (period === "AM") {
        if (hour === 12) {
            hour = 0; // Midnight case
        }
    } else if (period === "PM") {
        if (hour !== 12) {
            hour += 12; // Convert PM hours
        }
    }

    // Format hours, minutes, and seconds into a string
    const hh = hour.toString().padStart(2, "0");
    const mm = minute.toString().padStart(2, "0");
    const ss = "00"; // Seconds default to '00'

    return `${hh}:${mm}:${ss}`;
}
export function convertDDMMYYYYtoYYYYMMDD(value: string) {
    const [day, month, yearTime] = value.split("-");
    const [year, time] = yearTime.split("T");
    return `${year}-${month}-${day}T${time}`;
}

export function isValidTeamPrefix(input: string): boolean {
    const regex = /^[A-Z][A-Z0-9]{1,3}$/;
    return regex.test(input.toUpperCase());
}

export function isValidTaskNumber(input: string): boolean {
    // Regex breakdown:
    // ^[A-Za-z]        - Starts with a letter
    // [A-Za-z0-9]{1,3} - Followed by 1 to 3 alphanumeric characters (total length before "-" is at most 4)
    // -                - Exactly one dash "-"
    // [0-9]+           - At least one numeric character after "-"
    // $                - End of the string
    const regex = /^[A-Za-z][A-Za-z0-9]{1,3}-[0-9]+$/;
    return regex.test(input);
}

export function getTeamCode(teamPrefix: string | null | undefined, teamName: string | null | undefined): string | null {
    if (teamPrefix) {
        const trimmedTeamPrefix = teamPrefix.trim();
        if (isValidTeamPrefix(trimmedTeamPrefix)) {
            return trimmedTeamPrefix;
        }
    }
    if (teamName) {
        const trimmedTeamName = teamName.trim();
        if (isValidTeamPrefix(trimmedTeamName)) {
            return trimmedTeamName; // Assuming a swap might have occurred
        }
    }
    return null;
}

export function getNameOfTeam(
    teamName: string | null | undefined,
    teamPrefix: string | null | undefined,
): string | null {
    if (teamName) {
        const trimmedTeamName = teamName.trim();
        if (!isValidTeamPrefix(trimmedTeamName.toUpperCase())) {
            return trimmedTeamName;
        }
    }

    // Only consider teamPrefix if no valid teamName is provided.
    if (!teamName && teamPrefix) {
        const trimmedTeamPrefix = teamPrefix.trim();
        if (!isValidTeamPrefix(trimmedTeamPrefix.toUpperCase())) {
            return trimmedTeamPrefix;
        }
    }
    return teamName ? teamName.trim() : null;
}

export function minutesIntoHours(totalMinutes: number): string {
    const totalHours = totalMinutes / 60;
    const hasDecimal = totalHours % 1 !== 0;

    if (hasDecimal) {
        const integerPart = Math.trunc(totalHours);
        const floatPart = totalMinutes - integerPart * 60;
        return `${integerPart}h ${floatPart}m`;
    } else {
        return `${totalHours}h`;
    }
}

export function appendTextToField(existingText: string | null | undefined, nextTextToBeAppended: string): string {
    // Step 1: Handle null, undefined, or empty string cases
    if (!existingText || existingText.trim() === "") {
        return nextTextToBeAppended; // Simply replace existingText with nextTextToBeAppended if existingText is null, undefined, or empty
    }

    if (!nextTextToBeAppended || nextTextToBeAppended.trim() === "") {
        return existingText; // Return existingText if nextTextToBeAppended is null, undefined, or empty
    }

    // Step 2: Trim existingText and check the last character
    existingText = existingText.trim(); // Trim leading/trailing whitespace from existingText

    // Step 3: Check if the last character is a dot
    if (existingText.endsWith(".")) {
        return existingText + " " + nextTextToBeAppended; // If it ends with a dot, append nextTextToBeAppended with a space
    } else {
        return existingText + ". " + nextTextToBeAppended; // If it doesn't end with a dot, add a dot then append nextTextToBeAppended
    }
}

export function areArraysEqual<T>(array1: T[], array2: T[]): boolean {
    if (array1.length !== array2.length) {
        return false; // Early exit if lengths differ
    }

    // Sort arrays with a comparator for numbers and strings
    const sorted1 = [...array1].sort((a, b) => (a > b ? 1 : -1));
    const sorted2 = [...array2].sort((a, b) => (a > b ? 1 : -1));

    // Compare each value after sorting
    return sorted1.every((value, index) => value === sorted2[index]);
}

/** Returns a Date object from a string or Date object
 * @param date - Date string or Date object
 * @returns Date object or null if date is null
 */
export function getDateInstance(date: string | Date | null): Date | null {
    return date instanceof Date ? date : date ? new Date(date) : null;
}

/** Returns a formatted date-time string in the format "yyyy-MM-dd'T'HH:mm:ss"
 * @param date - Date object to format
 * @returns Formatted date-time string as "yyyy-MM-dd'T'HH:mm:ss" backend format
 **/
export function getFormattedDateTime(date: Date): string {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

/** Returns a formatted time string in the format "HH:mm:ss"
 * @param date - Date object to format
 * @returns Formatted time string as "HH:mm:ss" backend format
 **/
export function getFormattedTime(date: Date): string {
    return format(date, "HH:mm:ss");
}

export function smoothScrollTo(element: HTMLElement, to: number, duration = 300) {
    const start = element.scrollTop;
    const change = to - start;
    const startTime = performance.now();

    function animateScroll(currentTime: number) {
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);

        // Optional: easeInOutQuad for natural feel
        const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;

        element.scrollTop = start + change * ease;

        if (progress < 1) {
            requestAnimationFrame(animateScroll);
        }
    }

    requestAnimationFrame(animateScroll);
}

export function isValidDateRange(fromDate: Date | string | null, toDate: Date | string | null): boolean {
    const fromDateInstance = getDateInstance(fromDate);
    const toDateInstance = getDateInstance(toDate);

    // If both dates are null, consider it valid
    if (!fromDateInstance && !toDateInstance) {
        return true;
    }

    // If either date is null, return true (handled above)
    if (!fromDateInstance || !toDateInstance) {
        return true;
    }

    // Both dates exist, compare them
    return toDateInstance > fromDateInstance;
}

export function isQuillContentEmpty(html?: string | null): boolean {
    if (!html) return true;

    // If it has any media tags, treat as content
    if (/<img[^>]*>/i.test(html) || /<video[^>]*>/i.test(html) || /<iframe[^>]*>/i.test(html)) {
        return false;
    }

    // Remove Quill's empty paragraph(s) like <p><br></p>
    let cleaned = html.replace(/<p><br><\/p>/gi, '');

    // Strip all tags
    cleaned = cleaned.replace(/<[^>]*>/g, '');

    // Convert &nbsp; and trim whitespace
    cleaned = cleaned.replace(/&nbsp;/gi, ' ').trim();

    // If nothing left => considered empty
    return cleaned.length === 0;
}

export function getPlainText(htmlContent: string): string {
    if (!htmlContent) return "";

    let text: string = htmlContent;

    // 1. Replace <p> tags with a newline
    text = text.replace(/<\/p><p>/g, '\n');

    // 2. Replace <br> or <div> tags with newlines
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/div>/g, '\n');

    // 3. REMOVE ALL REMAINING TAGS (Pure Regex)
    text = text.replace(/<[^>]*>/g, '');

    // 4. DECODE HTML ENTITIES (like &nbsp; or &amp;)
    const entities: { [key: string]: string } = {
        '&nbsp;': ' ',
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"'
    };

    text = text.replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;/g, (match) => entities[match]);

    return text.trim();
}